import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, ArrowRight, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import WhatsAppLoginForm from "./WhatsAppLoginForm";

interface LoginFormProps {
  username: string;
  password: string;
  showPassword: boolean;
  helperText: string;
  loading: boolean;
  onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onShowPasswordToggle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: (email: string) => void;
  // WhatsApp OTP props
  whatsAppMobile?: string;
  whatsAppOTP?: string;
  onWhatsAppMobileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onWhatsAppOTPChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendWhatsAppOTP?: () => Promise<boolean | void>;
  onVerifyWhatsAppOTP?: (e: React.FormEvent) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  username,
  password,
  showPassword,
  helperText,
  loading,
  onUsernameChange,
  onPasswordChange,
  onShowPasswordToggle,
  onSubmit,
  whatsAppMobile = "",
  whatsAppOTP = "",
  onWhatsAppMobileChange = () => {},
  onWhatsAppOTPChange = () => {},
  onSendWhatsAppOTP = async () => true,
  onVerifyWhatsAppOTP = () => {},
}) => {
  const [activeTab, setActiveTab] = useState<"password" | "whatsapp">("password");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>("");

  const handleForgotPasswordSubmit = async () => {
    if (!email) {
      setError("Please enter your email.");
      return;
    }
    setError("");
    try {
      const response = await fetch("https://api.telegaruda.com/v1/users/forgot-password/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "56afdabec722ed15af82e69dcec9002a",
        },
        body: JSON.stringify({ email, role: "provider" }),
      });
      const result = await response.json();
      if (response.ok) {
        alert(result.message || "OTP sent successfully.");
        setIsForgotPassword(false);
      } else {
        setError(result.message || "Something went wrong.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };

  const SafeAnimatePresence = AnimatePresence as any;

  return (
    <div className="w-full space-y-6">
      {/* Modern Login Mode Tab Switcher */}
      {!isForgotPassword && (
        <div className="p-1 bg-slate-100/80 rounded-2xl flex items-center gap-1 border border-slate-200/60">
          <button
            type="button"
            onClick={() => setActiveTab("password")}
            className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-xs transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === "password"
                ? "bg-white text-slate-900 shadow-md shadow-slate-900/5"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
            }`}
          >
            <Lock size={15} className={activeTab === "password" ? "text-primary" : "text-slate-400"} />
            <span>Password Login</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("whatsapp")}
            className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-xs transition-all duration-200 flex items-center justify-center gap-2 relative ${
              activeTab === "whatsapp"
                ? "bg-white text-emerald-900 shadow-md shadow-emerald-900/5"
                : "text-slate-500 hover:text-emerald-700 hover:bg-emerald-50/50"
            }`}
          >
            <MessageSquare size={15} className={activeTab === "whatsapp" ? "text-emerald-600" : "text-slate-400"} />
            <span>WhatsApp OTP</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          </button>
        </div>
      )}

      <SafeAnimatePresence mode="wait">
        {activeTab === "whatsapp" && !isForgotPassword ? (
          <motion.div
            key="whatsapp-tab"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <WhatsAppLoginForm
              mobileNumber={whatsAppMobile}
              otp={whatsAppOTP}
              loading={loading}
              helperText={helperText}
              onMobileChange={onWhatsAppMobileChange}
              onOTPChange={onWhatsAppOTPChange}
              onSendOTP={onSendWhatsAppOTP}
              onVerifyOTP={onVerifyWhatsAppOTP}
            />
          </motion.div>
        ) : !isForgotPassword ? (
          <motion.form
            key="login"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-5"
            onSubmit={onSubmit}
          >
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Username</label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">
                  <User size={18} />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={onUsernameChange}
                  placeholder="Enter your username"
                  className={`w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 rounded-2xl outline-none transition-all duration-200 ${
                    helperText ? "border-rose-100 bg-rose-50/30 focus:border-rose-400" : "border-transparent focus:border-primary focus:bg-white focus:shadow-lg focus:shadow-primary/5"
                  }`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Password</label>
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-xs font-bold text-primary hover:text-primary-dark transition-colors"
                >
                  Forgot?
                </button>
              </div>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={onPasswordChange}
                  placeholder="Enter your password"
                  className={`w-full pl-12 pr-12 py-3.5 bg-slate-50 border-2 rounded-2xl outline-none transition-all duration-200 ${
                    helperText ? "border-rose-100 bg-rose-50/30 focus:border-rose-400" : "border-transparent focus:border-primary focus:bg-white focus:shadow-lg focus:shadow-primary/5"
                  }`}
                />
                <button
                  type="button"
                  onClick={onShowPasswordToggle}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {helperText && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-rose-500 mt-2 flex items-center gap-1.5 font-medium ml-1"
                >
                  <AlertCircle size={14} /> {helperText}
                </motion.p>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative py-4 px-6 bg-primary text-white font-bold rounded-2xl shadow-xl shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        ) : (
          <motion.div
            key="forgot"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Email Address</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:border-primary focus:bg-white outline-none transition-all duration-200"
                />
              </div>
              {error && <p className="text-xs text-rose-500 font-medium ml-1">{error}</p>}
            </div>

            <button
              type="button"
              onClick={handleForgotPasswordSubmit}
              disabled={loading}
              className="w-full py-4 px-6 bg-primary text-white font-bold rounded-2xl shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send Reset OTP"}
            </button>

            <button
              type="button"
              onClick={() => setIsForgotPassword(false)}
              className="w-full text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors"
            >
              Back to Sign In
            </button>
          </motion.div>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default LoginForm;
